import React, { useState, useEffect } from 'react';
import { User } from '../types/User';
import { Download, Users, Calendar, Search, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import { NetworkingService } from '../services/networkingService';
import { MeetingService } from '../services/meetingService';

interface AdminPanelProps {
  currentUser: User;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState<'users' | 'meetings'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    // OPTIMIZATION: Only load data if not already loaded (caching)
    if (!dataLoaded) {
      loadData();
    }
  }, [dataLoaded]);


  const loadData = async () => {
    setLoading(true);
    try {
      console.log('AdminPanel: Loading users...');
      // OPTIMIZATION: Load users and meetings in parallel instead of sequentially
      const [allUsers, allMeetings] = await Promise.all([
        NetworkingService.getAllUsersForAdmin(),
        loadAllMeetings()
      ]);
      
      console.log(`AdminPanel: Loaded ${allUsers.length} users`);
      setUsers(allUsers);
      setMeetings(allMeetings);
      setDataLoaded(true); // Mark data as loaded for caching
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
    setLoading(false);
  };

  const loadAllMeetings = async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // OPTIMIZATION: Simplified query - only fetch essential fields first
      const { data, error } = await supabase
        .from('meetings')
        .select(`
          id,
          requester_id,
          recipient_id,
          meeting_date,
          time_slot,
          table_number,
          subject,
          description,
          location,
          status,
          created_at,
          requester:users!meetings_requester_id_fkey(name, company, email),
          recipient:users!meetings_recipient_id_fkey(name, company, email)
        `)
        .order('meeting_date', { ascending: true })
        .order('time_slot', { ascending: true });

      if (error) {
        console.error('Error fetching all meetings:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching all meetings:', error);
      return [];
    }
  };

  const exportUsersToExcel = () => {
    const exportData = users.map(user => ({
      'Name': user.name,
      'Email': user.email,
      'Company': user.company,
      'Position': user.position,
      'Country': user.country,
      'About': user.about || '',
      'Role': user.role || 'user'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    
    const fileName = `logistics_market_users_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportUsersToCSV = () => {
    const headers = ['Name', 'Email', 'Company', 'Position', 'Country', 'About', 'Role'];
    const csvContent = [
      headers.join(','),
      ...users.map(user => [
        `"${user.name}"`,
        `"${user.email}"`,
        `"${user.company}"`,
        `"${user.position}"`,
        `"${user.country}"`,
        `"${user.about || ''}"`,
        `"${user.role || 'user'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `logistics_market_users_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportMeetingsToExcel = () => {
    const exportData = meetings.map(meeting => ({
      'Date': meeting.meeting_date,
      'Time': meeting.time_slot,
      'Table': meeting.table_number,
      'Subject': meeting.subject,
      'Description': meeting.description || '',
      'Location': meeting.location || '',
      'Status': meeting.status,
      'Requester Name': meeting.requester?.name || '',
      'Requester Company': meeting.requester?.company || '',
      'Requester Email': meeting.requester?.email || '',
      'Recipient Name': meeting.recipient?.name || '',
      'Recipient Company': meeting.recipient?.company || '',
      'Recipient Email': meeting.recipient?.email || '',
      'Created At': new Date(meeting.created_at).toLocaleString()
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Meetings');
    
    const fileName = `logistics_market_meetings_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportMeetingsToCSV = () => {
    const headers = ['Date', 'Time', 'Table', 'Subject', 'Description', 'Location', 'Status', 'Requester Name', 'Requester Company', 'Requester Email', 'Recipient Name', 'Recipient Company', 'Recipient Email', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...meetings.map(meeting => [
        `"${meeting.meeting_date}"`,
        `"${meeting.time_slot}"`,
        `"${meeting.table_number}"`,
        `"${meeting.subject}"`,
        `"${meeting.description || ''}"`,
        `"${meeting.location || ''}"`,
        `"${meeting.status}"`,
        `"${meeting.requester?.name || ''}"`,
        `"${meeting.requester?.company || ''}"`,
        `"${meeting.requester?.email || ''}"`,
        `"${meeting.recipient?.name || ''}"`,
        `"${meeting.recipient?.company || ''}"`,
        `"${meeting.recipient?.email || ''}"`,
        `"${new Date(meeting.created_at).toLocaleString()}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `logistics_market_meetings_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMeetings = meetings.filter(meeting =>
    meeting.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.requester?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.recipient?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
          <div className="text-gray-500">Loading admin data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Admin Panel</h1>
        <p className="text-gray-600">Manage users and meetings for Logistics Market Expo & Conference 2026</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'users'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('meetings')}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'meetings'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-2" />
          Meetings ({meetings.length})
        </button>
      </div>

      {/* Search and Export */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600"
          />
        </div>
        
        <button
          onClick={activeTab === 'users' ? exportUsersToExcel : exportMeetingsToExcel}
          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors ml-4"
          style={{ backgroundColor: '#C89116' }}
        >
          <Download className="w-4 h-4" />
          <span>Export to Excel</span>
        </button>
        <button
          onClick={activeTab === 'users' ? exportUsersToCSV : exportMeetingsToCSV}
          className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors ml-2"
          style={{ backgroundColor: '#28a745' }}
        >
          <Download className="w-4 h-4" />
          <span>Export to CSV</span>
        </button>
      </div>

      {/* Content */}
      {activeTab === 'users' ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3 overflow-hidden">
                          {user.avatar ? (
                            <img 
                              src={user.avatar} 
                              alt={user.name}
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <span className="text-sm font-semibold text-gray-600">
                              {user.name.split(" ").map(n => n[0]).join("")}
                            </span>
                          )}
                        </div>                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.company}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.position}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.country}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {user.role || 'user'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participants
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Table
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMeetings.map((meeting) => (
                  <tr key={meeting.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{meeting.meeting_date}</div>
                      <div className="text-gray-500">{meeting.time_slot}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{meeting.subject}</div>
                      {meeting.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {meeting.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div><strong>From:</strong> {meeting.requester?.name}</div>
                        <div className="text-gray-500">{meeting.requester?.company}</div>
                        <div className="mt-1"><strong>To:</strong> {meeting.recipient?.name}</div>
                        <div className="text-gray-500">{meeting.recipient?.company}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      Table {meeting.table_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        meeting.status === 'confirmed' 
                          ? 'bg-green-100 text-green-800'
                          : meeting.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : meeting.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {meeting.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;